import { Box, Typography } from '@mui/material';
import { StoryPageLayout } from '../layouts';

/**
 * Healthcare & Life Sciences Modernization Assessment
 *
 * This page showcases a business outcome story for HCLS modernization.
 * The STORY is the primary feature, demonstrating value through customer
 * success narrative. The form sidebar provides easy access for interested
 * prospects to request their own assessment.
 *
 * STRUCTURE:
 * - Dark header with page title
 * - Two-column layout (story content 2/3 left, form 1/3 right sticky)
 * - Customer overview section (enhanced with green accent)
 * - Business driver section (enhanced with green accent)
 * - Assessment approach section (enhanced with green accent)
 * - Business outcome section (enhanced with green accent)
 * - Form sidebar with sticky positioning (desktop) or stacked below (mobile)
 *
 * @returns {import('react').ReactElement} HCLS Modernization business outcome page
 */
function BusinessOutcomeHCLS() {
  return (
    <StoryPageLayout
      title="Healthcare & Life Sciences Modernization Assessment"
      subtitle="Advancing Metabolic Healthcare with AWS Cloud & AI Services"
      formHeading="Request Your Modernization Assessment"
      formFilePath="/forms/business-outcome-hcls.txt"
      heroImage="/images/healthcare-patient-portal-modernization.jpg"
      heroImageAlt="Healthcare patient portal modernization - cloud-based medical records and telehealth platform powered by AWS"
      errorMessage="Unable to load assessment form"
      loadingMessage="Loading assessment form..."
    >
      {/* Customer Section */}
      <Box
        sx={{
          mb: { xs: 6, md: 10 },
          borderLeft: {
            xs: '4px solid var(--color-primary)',
            md: '6px solid var(--color-primary)',
          },
          pl: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: 'var(--color-heading)',
            fontSize: { xs: '1.75rem', md: '2rem' },
            letterSpacing: '-0.02em',
          }}
        >
          Customer
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.0625rem',
            lineHeight: 1.75,
            color: 'var(--color-text)',
            fontWeight: 400,
          }}
        >
          An innovative healthcare organization managing dependent ventures in
          metabolic science, oncology, and digital therapeutics sought to
          modernize its technology foundation for scale. With a mission to
          personalize treatment and empower practitioners and patients through
          data, the company's leadership needed an AI-based platform capable of
          supporting both clinical research, patient portal and practitioners'
          applications.
        </Typography>
      </Box>

      {/* Visual separator - R logo */}
      <Box
        sx={{
          textAlign: 'center',
          my: 6,
        }}
      >
        <Box
          component="img"
          src="/images/r-logo.png"
          alt="Section separator"
          sx={{
            width: '40px',
            height: 'auto',
            opacity: 0.7,
          }}
        />
      </Box>

      {/* Business Driver Section */}
      <Box
        sx={{
          mb: { xs: 6, md: 10 },
          borderLeft: {
            xs: '4px solid var(--color-primary)',
            md: '6px solid var(--color-primary)',
          },
          pl: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: 'var(--color-heading)',
            fontSize: { xs: '1.75rem', md: '2rem' },
            letterSpacing: '-0.02em',
          }}
        >
          Business Driver
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.0625rem',
            lineHeight: 1.75,
            color: 'var(--color-text)',
            fontWeight: 400,
          }}
        >
          As the organization prepared for institutional funding and product
          expansion, it needed to move beyond its startup-level infrastructure
          toward a secure, compliant, and AI-ready cloud environment.
        </Typography>
      </Box>

      {/* Visual separator - R logo */}
      <Box
        sx={{
          textAlign: 'center',
          my: 6,
        }}
      >
        <Box
          component="img"
          src="/images/r-logo.png"
          alt="Section separator"
          sx={{
            width: '40px',
            height: 'auto',
            opacity: 0.7,
          }}
        />
      </Box>

      {/* Assessment Approach Section */}
      <Box
        sx={{
          mb: { xs: 6, md: 10 },
          borderLeft: {
            xs: '4px solid var(--color-primary)',
            md: '6px solid var(--color-primary)',
          },
          pl: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: 'var(--color-heading)',
            fontSize: { xs: '1.75rem', md: '2rem' },
            letterSpacing: '-0.02em',
          }}
        >
          Assessment Approach
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.0625rem',
            lineHeight: 1.75,
            color: 'var(--color-text)',
            mb: 3,
            fontWeight: 400,
          }}
        >
          RSM Collective's model combines our AWS Modernization Assessment
          framework, technology partners, and AWS service expertise into a
          unified delivery ecosystem known as The Collective. This approach
          enables us to address the diverse delivery needs of AI and data
          modernization projects while ensuring each assessment produces clear
          architectural guidance and actionable next steps. For this engagement,
          the team partnered with two AWS Advanced Partners:
        </Typography>
        <Box
          component="ul"
          sx={{
            pl: 0,
            listStyle: 'none',
            '& li': {
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              mb: 2,
              pl: 4,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '0.6em',
                width: '8px',
                height: '8px',
                backgroundColor: 'var(--color-primary)',
                borderRadius: '50%',
              },
              '& strong': {
                color: 'var(--color-heading)',
                fontWeight: 600,
              },
            },
          }}
        >
          <li>
            <strong>Nebulaworks</strong> – accelerated the design of the
            company's AI-enabled B2C health platform, applying DevOps
            discipline, metabolic domain expertise, and integrated data science
            to align architecture with business objectives and patient metabolic
            insight goals.
          </li>
        </Box>
      </Box>

      {/* Visual separator - R logo */}
      <Box
        sx={{
          textAlign: 'center',
          my: 6,
        }}
      >
        <Box
          component="img"
          src="/images/r-logo.png"
          alt="Section separator"
          sx={{
            width: '40px',
            height: 'auto',
            opacity: 0.7,
          }}
        />
      </Box>

      {/* Business Outcome Section */}
      <Box
        sx={{
          mb: { xs: 6, md: 10 },
          borderLeft: {
            xs: '4px solid var(--color-primary)',
            md: '6px solid var(--color-primary)',
          },
          pl: { xs: 3, md: 4 },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: 'var(--color-heading)',
            fontSize: { xs: '1.75rem', md: '2rem' },
            letterSpacing: '-0.02em',
          }}
        >
          Business Outcome
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.0625rem',
            lineHeight: 1.75,
            color: 'var(--color-text)',
            mb: 3,
            fontWeight: 400,
          }}
        >
          The RSM Collective Modernization Assessment delivered a targeted data
          and AI modernization strategy that aligned cloud architecture with the
          company's mission to scale metabolic healthcare.
        </Typography>
        <Box
          component="ul"
          sx={{
            pl: 0,
            listStyle: 'none',
            '& li': {
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              mb: 2,
              pl: 4,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '0.6em',
                width: '8px',
                height: '8px',
                backgroundColor: 'var(--color-primary)',
                borderRadius: '50%',
              },
            },
          }}
        >
          <li>
            Delivered modernization roadmap enabling launch readiness for a B2C
            health platform projected to drive multi-million-dollar ARR growth.
          </li>
          <li>
            Identified use of Amazon Comprehend Medical and Amazon Textract to
            automate extraction of biomarker and clinical insights from
            unstructured data.
          </li>
          <li>
            Defined end-to-end data architecture integrating Amazon SageMaker
            and Amazon Bedrock for AI-driven analytics and model deployment.
          </li>
          <li>
            Identified automation and SaaS integration opportunities to
            streamline operations across AWS and third-party systems.
          </li>
          <li>
            Established a governance and compliance framework aligned with
            HIPAA-eligible AWS services and GxP best practices, ensuring data
            integrity and security across research and clinical workloads.
          </li>
        </Box>
      </Box>
    </StoryPageLayout>
  );
}

export default BusinessOutcomeHCLS;
